/**
 * require-auth — guardas de ruta para zonas protegidas (F5a).
 *
 * `RequireAuth`: bloquea acceso a `/cuenta/*`. Mientras `loading=true` renderiza
 * un placeholder full-screen. Si no hay user redirige a `/auth/login`
 * pasando `from` en el state para que el login lo use al volver.
 *
 * `RequireAdmin`: extiende `RequireAuth` y además verifica `isAdmin`. Si el
 * usuario está autenticado pero no es admin, redirige a `/` mostrando un toast
 * "Acceso restringido" (solo una vez gracias a un ref).
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './auth-context';
import { useToast } from '../components/ui/ToastProvider';
import { Skeleton } from '../components/ui/Skeleton';
import { Routes } from '../lib/routes';

interface RequireAuthProps {
  children: ReactNode;
  /** Ruta a la que enviar si no hay sesión. Default `/auth/login`. */
  redirectTo?: string;
}

/** Placeholder full-screen mientras Supabase hidrata la sesión inicial. */
function AuthLoadingFallback() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Cargando"
      className="min-h-screen flex items-center justify-center bg-cream-100 px-4"
    >
      <div className="w-full max-w-md space-y-4">
        <Skeleton height={32} width="60%" rounded="md" />
        <Skeleton lines={3} />
        <Skeleton height={40} rounded="md" />
      </div>
    </div>
  );
}

export function RequireAuth({ children, redirectTo }: RequireAuthProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <AuthLoadingFallback />;

  if (!user) {
    return (
      <Navigate
        to={redirectTo ?? Routes.login}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  return <>{children}</>;
}

interface RequireAdminProps {
  children: ReactNode;
}

export function RequireAdmin({ children }: RequireAdminProps) {
  const { user, loading, isAdmin, profile } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  // Evita disparar el toast en cada re-render durante el ciclo de redirección.
  const warnedRef = useRef(false);

  // El profile puede tardar un tick en cargar tras un SIGNED_IN; mientras
  // tengamos user pero profile=null sin error, esperamos.
  const profileResolving = Boolean(user) && profile === null;

  useEffect(() => {
    if (!loading && !profileResolving && user && !isAdmin && !warnedRef.current) {
      warnedRef.current = true;
      toast({
        variant: 'warning',
        title: 'Acceso restringido',
        description: 'Necesitas permisos de administrador para entrar aquí.',
      });
    }
  }, [loading, profileResolving, user, isAdmin, toast]);

  if (loading || profileResolving) return <AuthLoadingFallback />;

  if (!user) {
    return (
      <Navigate
        to={Routes.login}
        state={{ from: location.pathname }}
        replace
      />
    );
  }

  if (!isAdmin) {
    return <Navigate to={Routes.home} replace />;
  }

  return <>{children}</>;
}
